"use client";

import axios from "axios";
import type {
  PlatformAdminLoginResponse,
  PlatformAdminStatsResponse,
  PlatformModuleListResponse,
  PlatformSubscriptionPlan,
  PlatformSubscriptionPlanDetailResponse,
  PlatformSubscriptionPlanResponse,
  PlatformSubscriptionRequestListResponse,
  PlatformSubscriptionReviewActionResponse,
  PlatformAddonRequestListResponse,
  PlatformAddonReviewActionResponse,
  PlatformPaymentReportListResponse,
  PlatformPaymentReportActionResponse,
  PlatformTenantListResponse,
  PlatformTenantModulesResponse,
  PlatformTrialModulesResponse,
  PlatformWalletAccountListResponse,
  PlatformWalletAdjustPayload,
  PlatformWalletAdjustmentResponse,
  PlatformWalletTransactionResponse,
  MenuTreeResponse,
  MenuSectionNode,
  TenantMenuOverrideResponse,
  PlatformIntegrationRequestListResponse,
  PlatformIntegrationRequestDetailResponse,
  PlatformIntegrationRequestActionResponse
} from "@repo/types";
import { resolveApiBaseUrl } from "./base/http";
import { platformAdminSessionStorage } from "../../lib/src/auth/platform-admin-session";

interface PlatformAdminLoginPayload {
  username: string;
  password: string;
  remember?: boolean;
}

interface TenantListQuery {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  sort_by?: string;
  sort_order?: "ASC" | "DESC";
}

interface UpdateTenantSubscriptionPayload {
  plan_id: string;
  billing_cycle?: "monthly" | "annual";
  custom_price?: number;
  expires_at?: string;
  payment_method?: string;
  notes?: string;
}

interface UpdateTenantModulePayload {
  is_enabled: boolean;
  custom_config?: Record<string, unknown>;
  reason?: string;
}

interface WalletAccountListQuery {
  search?: string;
  tenant_id?: string;
  customer_id?: string;
  page?: number;
  page_size?: number;
}

interface SubscriptionRequestQuery {
  status?: string;
  page?: number;
  limit?: number;
}

interface SubscriptionReviewActionPayload {
  note?: string;
  payment_reference?: string;
  reason?: string;
}

type PlanShape = Omit<PlatformSubscriptionPlan, "id" | "plan_code" | "included_modules" | "subscriber_count" | "monthly_revenue">;

interface PlanMutationPayload extends Partial<PlanShape> {
  plan_code?: string;
  included_modules?: string[];
  features?: PlanShape["features"];
}

interface CreatePlanPayload extends PlanMutationPayload {
  plan_code: string;
  name: string;
  monthly_price: number;
}

const platformAdminClient = axios.create({
  baseURL: resolveApiBaseUrl(),
  timeout: 15000
});

platformAdminClient.interceptors.request.use((config) => {
  const token = platformAdminSessionStorage.getToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers["X-Platform-Admin-Authorization"] = `Bearer ${token}`;
  }
  return config;
});

platformAdminClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      platformAdminSessionStorage.clearSession();
    }
    throw error;
  }
);

export const loginPlatformAdmin = async (payload: PlatformAdminLoginPayload) => {
  const { data } = await platformAdminClient.post<PlatformAdminLoginResponse>(
    "/api/platform-admin/auth/login",
    payload
  );
  if (data.success) {
    platformAdminSessionStorage.setSession(
      { token: data.token, admin: data.admin },
      { remember: Boolean(payload.remember) }
    );
  }
  return data;
};

export const logoutPlatformAdmin = async () => {
  platformAdminSessionStorage.clearSession();
};

export const fetchPlatformAdminStats = async () => {
  const { data } = await platformAdminClient.get<PlatformAdminStatsResponse>(
    "/api/platform-admin/stats"
  );
  return data;
};

export const fetchPlatformTenants = async (params?: TenantListQuery) => {
  const { data } = await platformAdminClient.get<PlatformTenantListResponse>(
    "/api/platform-admin/tenants",
    { params }
  );
  return data;
};

export const fetchPlatformTenantModules = async (tenantId: string) => {
  const { data } = await platformAdminClient.get<PlatformTenantModulesResponse>(
    `/api/platform-admin/tenants/${tenantId}/modules`
  );
  return data;
};

export const updatePlatformTenantModule = async (
  tenantId: string,
  moduleId: string,
  payload: UpdateTenantModulePayload
) => {
  const { data } = await platformAdminClient.put(
    `/api/platform-admin/tenants/${tenantId}/modules/${moduleId}`,
    payload
  );
  return data;
};

export const updatePlatformTenantSubscription = async (
  tenantId: string,
  payload: UpdateTenantSubscriptionPayload
) => {
  const { data } = await platformAdminClient.put(
    `/api/platform-admin/tenants/${tenantId}/subscription`,
    payload
  );
  return data;
};

export const cancelPlatformTenantSubscription = async (
  tenantId: string,
  payload?: { reason?: string }
) => {
  const { data } = await platformAdminClient.post(
    `/api/platform-admin/tenants/${tenantId}/cancel-subscription`,
    payload ?? {}
  );
  return data;
};

export const fetchPlatformSubscriptionPlans = async () => {
  const { data } = await platformAdminClient.get<PlatformSubscriptionPlanResponse>(
    "/api/platform-admin/subscription-plans"
  );
  return data;
};

export const fetchPlatformModules = async () => {
  const { data } = await platformAdminClient.get<PlatformModuleListResponse>(
    "/api/platform-admin/modules"
  );
  return data;
};

export const fetchSubscriptionRequests = async (params?: SubscriptionRequestQuery) => {
  const { data } = await platformAdminClient.get<PlatformSubscriptionRequestListResponse>(
    "/api/platform-admin/subscription-requests",
    { params }
  );
  return data;
};

export const approveSubscriptionRequest = async (
  transactionId: string,
  payload?: SubscriptionReviewActionPayload
) => {
  const { data } = await platformAdminClient.post<PlatformSubscriptionReviewActionResponse>(
    `/api/platform-admin/subscription-requests/${transactionId}/approve`,
    payload ?? {}
  );
  return data;
};

export const rejectSubscriptionRequest = async (
  transactionId: string,
  payload?: SubscriptionReviewActionPayload
) => {
  const { data } = await platformAdminClient.post<PlatformSubscriptionReviewActionResponse>(
    `/api/platform-admin/subscription-requests/${transactionId}/reject`,
    payload ?? {}
  );
  return data;
};

export const cancelSubscriptionRequest = async (
  transactionId: string,
  payload?: SubscriptionReviewActionPayload
) => {
  const { data } = await platformAdminClient.post<PlatformSubscriptionReviewActionResponse>(
    `/api/platform-admin/subscription-requests/${transactionId}/cancel`,
    payload ?? {}
  );
  return data;
};

export const fetchSubscriptionAddonRequests = async (params?: { status?: string; limit?: number }) => {
  const { data } = await platformAdminClient.get<PlatformAddonRequestListResponse>(
    "/api/platform-admin/subscription-addon-requests",
    { params }
  );
  return data;
};

export const requestSubscriptionAddonPayment = async (requestId: string, payload?: { note?: string }) => {
  const { data } = await platformAdminClient.post<PlatformAddonReviewActionResponse>(
    `/api/platform-admin/subscription-addon-requests/${requestId}/request-payment`,
    payload ?? {}
  );
  return data;
};

export const approveSubscriptionAddonRequest = async (requestId: string, payload?: { note?: string }) => {
  const { data } = await platformAdminClient.post<PlatformAddonReviewActionResponse>(
    `/api/platform-admin/subscription-addon-requests/${requestId}/approve`,
    payload ?? {}
  );
  return data;
};

export const rejectSubscriptionAddonRequest = async (requestId: string, payload?: { reason?: string }) => {
  const { data } = await platformAdminClient.post<PlatformAddonReviewActionResponse>(
    `/api/platform-admin/subscription-addon-requests/${requestId}/reject`,
    payload ?? {}
  );
  return data;
};

export const fetchPlatformPaymentReports = async (params?: { status?: string; limit?: number }) => {
  const { data } = await platformAdminClient.get<PlatformPaymentReportListResponse>(
    "/api/platform-admin/payment-reports",
    { params }
  );
  return data;
};

export const fetchTenantPaymentReports = async (tenantId: string) => {
  const { data } = await platformAdminClient.get<PlatformPaymentReportListResponse>(
    `/api/platform-admin/tenants/${tenantId}/payment-reports`
  );
  return data;
};

export const confirmPaymentReport = async (reportId: string, payload?: { note?: string }) => {
  const { data } = await platformAdminClient.post<PlatformPaymentReportActionResponse>(
    `/api/platform-admin/payment-reports/${reportId}/confirm`,
    payload ?? {}
  );
  return data;
};

export const rejectPaymentReport = async (reportId: string, payload?: { reason?: string }) => {
  const { data } = await platformAdminClient.post<PlatformPaymentReportActionResponse>(
    `/api/platform-admin/payment-reports/${reportId}/reject`,
    payload ?? {}
  );
  return data;
};

const normalizePlanPayload = (payload: PlanMutationPayload) => {
  const { included_modules, ...rest } = payload;
  const normalized = Object.fromEntries(
    Object.entries(rest).filter(([, value]) => value !== undefined)
  );
  if (included_modules !== undefined) {
    return {
      ...normalized,
      included_modules: { modules: included_modules }
    };
  }
  return normalized;
};

export const createPlatformSubscriptionPlan = async (payload: CreatePlanPayload) => {
  const { data } = await platformAdminClient.post<PlatformSubscriptionPlanDetailResponse>(
    "/api/platform-admin/subscription-plans",
    normalizePlanPayload(payload)
  );
  return data;
};

export const updatePlatformSubscriptionPlan = async (
  planId: string,
  payload: PlanMutationPayload
) => {
  const { data } = await platformAdminClient.put<PlatformSubscriptionPlanDetailResponse>(
    `/api/platform-admin/subscription-plans/${planId}`,
    normalizePlanPayload(payload)
  );
  return data;
};

export const syncPlatformSubscriptionPlanToTenants = async (planId: string) => {
  const { data } = await platformAdminClient.post<{
    success: boolean;
    message: string;
    synced_subscriptions: number;
    synced_users: number;
  }>(`/api/platform-admin/subscription-plans/${planId}/sync-tenants`);
  return data;
};

export const deletePlatformSubscriptionPlan = async (planId: string) => {
  const { data } = await platformAdminClient.delete<{ success: boolean; message: string }>(
    `/api/platform-admin/subscription-plans/${planId}`
  );
  return data;
};

export const fetchTrialModulesConfig = async () => {
  const { data } = await platformAdminClient.get<PlatformTrialModulesResponse>(
    "/api/platform-admin/trial-modules"
  );
  return data;
};

export const updateTrialModulesConfig = async (modules: string[]) => {
  const { data } = await platformAdminClient.put<PlatformTrialModulesResponse>(
    "/api/platform-admin/trial-modules",
    { modules }
  );
  return data;
};

// ─── Trial Feature Toggles ───

export interface TrialFeatureToggles {
  enable_salary: boolean;
  enable_personal_calendar: boolean;
  enable_deposit: boolean;
  enable_reminder: boolean;
  enable_extra_slot: boolean;
  enable_assistant: boolean;
  enable_product_commission: boolean;
  enable_multi_locale: boolean;
  enable_loyalty_points: boolean;
  enable_birthday_reward: boolean;
  enable_full_payment: boolean;
  enable_no_show_deposit: boolean;
  enable_member_tier: boolean;
}

export const fetchTrialFeatureToggles = async (): Promise<TrialFeatureToggles> => {
  const { data } = await platformAdminClient.get<{ success: boolean; toggles: TrialFeatureToggles }>(
    "/api/platform-admin/trial-feature-toggles"
  );
  return data.toggles;
};

export const updateTrialFeatureToggles = async (toggles: TrialFeatureToggles) => {
  const { data } = await platformAdminClient.put<{ success: boolean; toggles: TrialFeatureToggles }>(
    "/api/platform-admin/trial-feature-toggles",
    { toggles }
  );
  return data;
};

// ─── Trial Marketing Display ───

export interface TrialMarketingDisplay {
  /** 行銷網站要列出的功能（enable_* 欄位名）；null = 沿用預設（全部列出） */
  features: string[] | null;
  /** 行銷網站要列出的模組代碼 */
  modules: string[];
}

export const fetchTrialMarketingDisplay = async (): Promise<TrialMarketingDisplay> => {
  const { data } = await platformAdminClient.get<{ success: boolean; display: TrialMarketingDisplay }>(
    "/api/platform-admin/trial-marketing-display"
  );
  return data.display;
};

export const updateTrialMarketingDisplay = async (display: TrialMarketingDisplay) => {
  const { data } = await platformAdminClient.put<{ success: boolean; display: TrialMarketingDisplay }>(
    "/api/platform-admin/trial-marketing-display",
    display
  );
  return data;
};

// ─── Trial Resource Limits ───

export interface TrialLimitsConfig {
  max_staffs: number;
  max_services: number;
  max_products: number;
  max_customers: number;
  max_appointments_per_month: number;
  max_devices: number;
}

export interface TrialLimitsResponse {
  success: boolean;
  limits: TrialLimitsConfig;
  meta?: { updated_at?: string; updated_by?: { id: string; name: string } };
}

export const fetchTrialLimitsConfig = async () => {
  const { data } = await platformAdminClient.get<TrialLimitsResponse>(
    "/api/platform-admin/trial-limits"
  );
  return data;
};

export const updateTrialLimitsConfig = async (limits: TrialLimitsConfig) => {
  const { data } = await platformAdminClient.put<TrialLimitsResponse>(
    "/api/platform-admin/trial-limits",
    limits
  );
  return data;
};

// ─── 行銷網站（brand-website）文字內容 ───
// 只管理官網文案，方案本身（名稱／價格／額度）仍透過 plans API 維護

export interface WebsiteContentSectionItem {
  title: string;
  description: string;
}

export interface WebsiteContent {
  home: {
    hero: {
      eyebrow: string;
      title: string;
      subtitle: string;
      primaryButtonLabel: string;
      secondaryButtonLabel: string;
      chips: string[];
    };
    trustBar: { items: string[] };
    howItWorks: {
      pill: string;
      title: string;
      subtitle: string;
      ctaButtonLabel: string;
      steps: WebsiteContentSectionItem[];
    };
    results: {
      pill: string;
      title: string;
      subtitle: string;
      items: Array<{ label: string; value: string; description: string }>;
    };
    industries: { pill: string; title: string; subtitle: string };
    features: {
      pill: string;
      title: string;
      subtitle: string;
      items: Array<{ title: string; description: string; details: string; caption: string }>;
    };
    replaces: {
      pill: string;
      title: string;
      subtitle: string;
      calloutText: string;
      primaryButtonLabel: string;
      secondaryButtonLabel: string;
      items: Array<{ before: string; afterTitle: string; afterDescription: string }>;
    };
    screens: {
      pill: string;
      title: string;
      subtitle: string;
      items: Array<{ caption: string; title: string; description: string }>;
    };
    savingsCalculator: {
      pill: string;
      title: string;
      subtitle: string;
      ctaLongLabel: string;
      ctaShortLabel: string;
      disclaimer: string;
    };
    pricingPreview: { pill: string; title: string; subtitle: string; ctaLabel: string };
    faq: {
      pill: string;
      title: string;
      subtitle: string;
      ctaLabel: string;
      items: Array<{ question: string; answer: string }>;
    };
    cta: {
      eyebrow: string;
      title: string;
      subtitle: string;
      primaryButtonLabel: string;
      secondaryButtonLabel: string;
      features: WebsiteContentSectionItem[];
    };
  };
  pricing: {
    pageHeader: { eyebrow: string; title: string; description: string };
    loadingText: string;
    emptyStateText: string;
    trialCard: {
      badge: string;
      title: string;
      description: string;
      primaryButtonLabel: string;
      secondaryButtonLabel: string;
    };
    common: {
      startUsingLabel: string;
      consultLabel: string;
      freeTrialLabel: string;
      recommendedBadge: string;
    };
    compareSection: {
      title: string;
      subtitle: string;
      limitsSectionLabel: string;
      featuresSectionLabel: string;
    };
    addonsSection: { title: string; subtitle: string; notAvailableLabel: string };
  };
  shared: {
    navbar: { logoText: string; pricingLinkLabel: string; consultLinkLabel: string; ctaLabel: string };
    footer: {
      brandName: string;
      tagline: string;
      instagramHandle: string;
      productSectionTitle: string;
      pricingLinkLabel: string;
      downloadLinkLabel: string;
      supportSectionTitle: string;
      consultLinkLabel: string;
      appCtaTitle: string;
      appCtaSubtitle: string;
    };
  };
}

export interface WebsiteContentResponse {
  success: boolean;
  content: WebsiteContent;
  meta?: { updated_at?: string; updated_by?: { id: string; name: string } };
}

export const fetchWebsiteContentConfig = async () => {
  const { data } = await platformAdminClient.get<WebsiteContentResponse>(
    "/api/platform-admin/website-content"
  );
  return data;
};

export const updateWebsiteContentConfig = async (content: WebsiteContent) => {
  const { data } = await platformAdminClient.put<WebsiteContentResponse>(
    "/api/platform-admin/website-content",
    { content }
  );
  return data;
};

export const fetchWalletAccounts = async (params?: WalletAccountListQuery) => {
  const { data } = await platformAdminClient.get<PlatformWalletAccountListResponse>(
    "/api/platform-admin/wallet-accounts",
    { params }
  );
  return data;
};

export const fetchPlatformWalletTransactions = async (walletId: string, params?: { limit?: number }) => {
  const { data } = await platformAdminClient.get<PlatformWalletTransactionResponse>(
    `/api/platform-admin/wallet-accounts/${walletId}/transactions`,
    { params }
  );
  return data;
};

export const adjustWalletAccount = async (payload: PlatformWalletAdjustPayload) => {
  const { data } = await platformAdminClient.post<PlatformWalletAdjustmentResponse>(
    "/api/platform-admin/wallet-accounts/adjust",
    payload
  );
  return data;
};

// ─── Payment Methods ───

export interface PlatformPaymentMethodFull {
  id: string;
  code: string;
  label: string;
  description: string;
  is_active: boolean;
  sort_order: number;
  requires_config: boolean;
  config_label: string;
  created_at: string;
  updated_at: string;
}

interface PaymentMethodCreatePayload {
  code: string;
  label: string;
  description?: string;
  requires_config?: boolean;
  config_label?: string;
  sort_order?: number;
}

interface PaymentMethodUpdatePayload {
  label?: string;
  description?: string;
  is_active?: boolean;
  sort_order?: number;
  requires_config?: boolean;
  config_label?: string;
}

export const fetchPlatformPaymentMethods = async () => {
  const { data } = await platformAdminClient.get<{ success: boolean; methods: PlatformPaymentMethodFull[] }>(
    "/api/platform-admin/payment-methods"
  );
  return data;
};

export const createPlatformPaymentMethod = async (payload: PaymentMethodCreatePayload) => {
  const { data } = await platformAdminClient.post<{ success: boolean; method: PlatformPaymentMethodFull; message: string }>(
    "/api/platform-admin/payment-methods",
    payload
  );
  return data;
};

export const updatePlatformPaymentMethod = async (id: string, payload: PaymentMethodUpdatePayload) => {
  const { data } = await platformAdminClient.put<{ success: boolean; method: PlatformPaymentMethodFull; message: string }>(
    `/api/platform-admin/payment-methods/${id}`,
    payload
  );
  return data;
};

// ─── 訂閱收款方式（租戶向平台付款） ───

export interface SubscriptionPaymentChannelFull {
  id: string;
  code: string;
  label: string;
  description: string;
  channel_type: "manual" | "gateway";
  provider: string | null;
  reference_label: string;
  reference_required: boolean;
  reference_digits: number | null;
  account_details: Array<{ label: string; value: string }>;
  is_active: boolean;
  sort_order: number;
}

export interface SubscriptionPaymentChannelPayload {
  code?: string;
  label?: string;
  description?: string;
  reference_label?: string;
  reference_required?: boolean;
  reference_digits?: number | null;
  account_details?: Array<{ label: string; value: string }>;
  is_active?: boolean;
  sort_order?: number;
}

export const fetchSubscriptionPaymentChannelsAdmin = async () => {
  const { data } = await platformAdminClient.get<{ success: boolean; channels: SubscriptionPaymentChannelFull[] }>(
    "/api/platform-admin/subscription-payment-channels"
  );
  return data;
};

export const createSubscriptionPaymentChannel = async (payload: SubscriptionPaymentChannelPayload) => {
  const { data } = await platformAdminClient.post<{ success: boolean; channel: SubscriptionPaymentChannelFull; message: string }>(
    "/api/platform-admin/subscription-payment-channels",
    payload
  );
  return data;
};

export const updateSubscriptionPaymentChannel = async (id: string, payload: SubscriptionPaymentChannelPayload) => {
  const { data } = await platformAdminClient.put<{ success: boolean; channel: SubscriptionPaymentChannelFull; message: string }>(
    `/api/platform-admin/subscription-payment-channels/${id}`,
    payload
  );
  return data;
};

// ─── LINE Pay 租戶串接設定 ───

export interface TenantLinepayConfig {
  id: string;
  tenant_id: string;
  tenant_name?: string;
  channel_id: string;
  channel_secret: string;
  is_sandbox: boolean;
  is_verified: boolean;
  is_enabled: boolean;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

interface LinepayConfigPayload {
  channel_id: string;
  channel_secret: string;
  is_sandbox?: boolean;
}

export const fetchLinepayConfigs = async () => {
  const { data } = await platformAdminClient.get<{ success: boolean; configs: TenantLinepayConfig[] }>(
    "/api/platform-admin/linepay/configs"
  );
  return data;
};

export const fetchLinepayConfig = async (tenantId: string) => {
  const { data } = await platformAdminClient.get<{ success: boolean; config: TenantLinepayConfig | null }>(
    `/api/platform-admin/linepay/configs/${tenantId}`
  );
  return data;
};

export const saveLinepayConfig = async (tenantId: string, payload: LinepayConfigPayload) => {
  const { data } = await platformAdminClient.put<{ success: boolean; config: TenantLinepayConfig; message: string }>(
    `/api/platform-admin/linepay/configs/${tenantId}`,
    payload
  );
  return data;
};

export const verifyLinepayConfig = async (tenantId: string) => {
  const { data } = await platformAdminClient.post<{ success: boolean; config?: TenantLinepayConfig; message: string }>(
    `/api/platform-admin/linepay/configs/${tenantId}/verify`
  );
  return data;
};

export const toggleLinepayConfig = async (tenantId: string, is_enabled: boolean) => {
  const { data } = await platformAdminClient.put<{ success: boolean; config: TenantLinepayConfig; message: string }>(
    `/api/platform-admin/linepay/configs/${tenantId}/toggle`,
    { is_enabled }
  );
  return data;
};

// ─── 刷卡租戶串接設定 ───

export interface TenantCreditcardConfig {
  id: string;
  tenant_id: string;
  tenant_name?: string;
  merchant_id: string;
  terminal_id: string;
  is_sandbox: boolean;
  is_verified: boolean;
  is_enabled: boolean;
  verified_at: string | null;
  created_at: string;
  updated_at: string;
}

interface CreditcardConfigPayload {
  merchant_id: string;
  terminal_id: string;
  is_sandbox?: boolean;
}

export const fetchCreditcardConfigs = async () => {
  const { data } = await platformAdminClient.get<{ success: boolean; configs: TenantCreditcardConfig[] }>(
    "/api/platform-admin/creditcard/configs"
  );
  return data;
};

export const fetchCreditcardConfig = async (tenantId: string) => {
  const { data } = await platformAdminClient.get<{ success: boolean; config: TenantCreditcardConfig | null }>(
    `/api/platform-admin/creditcard/configs/${tenantId}`
  );
  return data;
};

export const saveCreditcardConfig = async (tenantId: string, payload: CreditcardConfigPayload) => {
  const { data } = await platformAdminClient.put<{ success: boolean; config: TenantCreditcardConfig; message: string }>(
    `/api/platform-admin/creditcard/configs/${tenantId}`,
    payload
  );
  return data;
};

export const verifyCreditcardConfig = async (tenantId: string) => {
  const { data } = await platformAdminClient.post<{ success: boolean; config?: TenantCreditcardConfig; message: string }>(
    `/api/platform-admin/creditcard/configs/${tenantId}/verify`
  );
  return data;
};

export const toggleCreditcardConfig = async (tenantId: string, is_enabled: boolean) => {
  const { data } = await platformAdminClient.put<{ success: boolean; config: TenantCreditcardConfig; message: string }>(
    `/api/platform-admin/creditcard/configs/${tenantId}/toggle`,
    { is_enabled }
  );
  return data;
};

// ---- Integration Requests (串接申請) ----

interface IntegrationRequestListQuery {
  status?: string;
  page?: number;
  limit?: number;
}

interface VerifyIntegrationResult {
  success: boolean;
  verified: boolean;
  botInfo?: Record<string, unknown>;
  message?: string;
}

export const createIntegrationRequestForTenant = async (tenantId: string, provider = 'line') => {
  const { data } = await platformAdminClient.post<PlatformIntegrationRequestActionResponse>(
    '/api/platform-admin/integration-requests/create-for-tenant',
    { tenant_id: tenantId, provider }
  );
  return data;
};

export const fetchIntegrationRequests = async (params?: IntegrationRequestListQuery) => {
  const { data } = await platformAdminClient.get<PlatformIntegrationRequestListResponse>(
    "/api/platform-admin/integration-requests",
    { params }
  );
  return data;
};

export const fetchIntegrationRequestDetail = async (id: string) => {
  const { data } = await platformAdminClient.get<PlatformIntegrationRequestDetailResponse>(
    `/api/platform-admin/integration-requests/${id}`
  );
  return data;
};

export const configureIntegrationRequest = async (
  id: string,
  credentials: Record<string, string>
) => {
  const { data } = await platformAdminClient.post<PlatformIntegrationRequestActionResponse>(
    `/api/platform-admin/integration-requests/${id}/configure`,
    { credentials }
  );
  return data;
};

export const verifyIntegrationRequest = async (id: string) => {
  const { data } = await platformAdminClient.post<VerifyIntegrationResult>(
    `/api/platform-admin/integration-requests/${id}/verify`
  );
  return data;
};

export const completeIntegrationRequest = async (id: string, adminNote?: string) => {
  const { data } = await platformAdminClient.post<PlatformIntegrationRequestActionResponse>(
    `/api/platform-admin/integration-requests/${id}/complete`,
    { admin_note: adminNote }
  );
  return data;
};

export const rejectIntegrationRequest = async (id: string, adminNote?: string) => {
  const { data } = await platformAdminClient.post<PlatformIntegrationRequestActionResponse>(
    `/api/platform-admin/integration-requests/${id}/reject`,
    { admin_note: adminNote }
  );
  return data;
};

export const reconfigureIntegrationRequest = async (id: string) => {
  const { data } = await platformAdminClient.post<PlatformIntegrationRequestActionResponse>(
    `/api/platform-admin/integration-requests/${id}/reconfigure`
  );
  return data;
};

// ---- Menu Management ----

export const fetchGlobalMenuTree = async () => {
  const { data } = await platformAdminClient.get<MenuTreeResponse>(
    "/api/platform-admin/menu-tree"
  );
  return data;
};

export const saveGlobalMenuTree = async (sections: MenuSectionNode[]) => {
  const { data } = await platformAdminClient.put<{ success: boolean; message: string }>(
    "/api/platform-admin/menu-tree",
    { sections }
  );
  return data;
};

export const createMenuSection = async (payload: { section_key: string; label: string; icon?: string }) => {
  const { data } = await platformAdminClient.post<{ success: boolean; section: MenuSectionNode; message: string }>(
    "/api/platform-admin/menu-sections",
    payload
  );
  return data;
};

export const updateMenuSection = async (id: string, payload: { label?: string; icon?: string }) => {
  const { data } = await platformAdminClient.put<{ success: boolean; section: MenuSectionNode; message: string }>(
    `/api/platform-admin/menu-sections/${id}`,
    payload
  );
  return data;
};

export const deleteMenuSection = async (id: string) => {
  const { data } = await platformAdminClient.delete<{ success: boolean; message: string }>(
    `/api/platform-admin/menu-sections/${id}`
  );
  return data;
};

export const fetchTenantMenuOverrides = async (tenantId: string) => {
  const { data } = await platformAdminClient.get<TenantMenuOverrideResponse>(
    `/api/platform-admin/tenants/${tenantId}/menu-overrides`
  );
  return data;
};

export const saveTenantMenuOverrides = async (tenantId: string, menuTree: MenuSectionNode[] | null) => {
  const { data } = await platformAdminClient.put<{ success: boolean; message: string }>(
    `/api/platform-admin/tenants/${tenantId}/menu-overrides`,
    { menu_tree: menuTree }
  );
  return data;
};

/* ─── Support Tickets (Admin) ─── */

export interface AdminSupportTicket {
  id: string;
  tenant_id: string;
  tenant_name?: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  subject: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  created_at: string;
  updated_at: string;
  messages: AdminSupportMessage[];
}

export interface AdminSupportMessage {
  id: string;
  ticket_id: string;
  sender_type: "tenant" | "admin";
  sender_name: string;
  content: string;
  created_at: string;
}

interface AdminTicketListQuery {
  status?: string;
  limit?: number;
}

const fetchAdminSupportTickets = async (params?: AdminTicketListQuery): Promise<{ tickets: AdminSupportTicket[] }> => {
  const { data } = await platformAdminClient.get<{ success: boolean; tickets: AdminSupportTicket[] }>(
    "/api/platform-admin/support/tickets",
    { params }
  );
  return data;
};

const fetchAdminSupportTicket = async (id: string): Promise<AdminSupportTicket> => {
  const { data } = await platformAdminClient.get<{ success: boolean; ticket: AdminSupportTicket }>(
    `/api/platform-admin/support/tickets/${id}`
  );
  return data.ticket;
};

const replyAdminSupportTicket = async (id: string, message: string): Promise<AdminSupportTicket> => {
  const { data } = await platformAdminClient.post<{ success: boolean; ticket: AdminSupportTicket }>(
    `/api/platform-admin/support/tickets/${id}/reply`,
    { message }
  );
  return data.ticket;
};

const updateAdminTicketStatus = async (id: string, status: string): Promise<AdminSupportTicket> => {
  const { data } = await platformAdminClient.put<{ success: boolean; ticket: AdminSupportTicket }>(
    `/api/platform-admin/support/tickets/${id}/status`,
    { status }
  );
  return data.ticket;
};

const executeAdminAccountDeletion = async (id: string): Promise<{
  ticket: AdminSupportTicket | null;
  deleted: {
    user_id: string;
    email: string;
    role: string;
    scope: "tenant" | "user";
    staff_ids: string[];
    staff_user_ids: string[];
  };
  message: string;
}> => {
  const { data } = await platformAdminClient.post<{
    success: boolean;
    ticket: AdminSupportTicket | null;
    deleted: {
      user_id: string;
      email: string;
      role: string;
      scope: "tenant" | "user";
      staff_ids: string[];
      staff_user_ids: string[];
    };
    message: string;
  }>(`/api/platform-admin/support/tickets/${id}/execute-account-deletion`);
  return {
    ticket: data.ticket,
    deleted: data.deleted,
    message: data.message,
  };
};

// ── User Management ──

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: string;
  display_name: string | null;
  photo_url: string | null;
  subscription_status: string | null;
  current_plan_id: string | null;
  subscription_expires_at: string | null;
  trial_expires_at: string | null;
  billing_cycle: string | null;
  subscribed_monthly_price: number | null;
  subscription_started_at: string | null;
  staff_id: string | null;
  created_at: string;
  updated_at: string;
  staff_name?: string;
  staff_display_name?: string;
}

const fetchAdminUsers = async (params?: { search?: string; role?: string; status?: string; page?: number; limit?: number }) => {
  const { data } = await platformAdminClient.get<{ success: boolean; users: AdminUser[]; total: number }>("/api/platform-admin/users", { params });
  return data;
};

const fetchAdminUser = async (userId: string) => {
  const { data } = await platformAdminClient.get<{ success: boolean; user: AdminUser }>(`/api/platform-admin/users/${userId}`);
  return data.user;
};

const updateAdminUser = async (userId: string, payload: Partial<Pick<AdminUser, "name" | "email" | "phone" | "role" | "display_name">>) => {
  const { data } = await platformAdminClient.put<{ success: boolean; user: AdminUser }>(`/api/platform-admin/users/${userId}`, payload);
  return data.user;
};

const resetAdminUserPassword = async (userId: string, password: string) => {
  const { data } = await platformAdminClient.put<{ success: boolean; message: string }>(`/api/platform-admin/users/${userId}/password`, { password });
  return data;
};

export interface PurgePersonalCalendarResult {
  reminderLogs: number;
  events: number;
  projects: number;
  categories: number;
  shares: number;
}

const purgeAdminUserPersonalCalendar = async (userId: string) => {
  const { data } = await platformAdminClient.delete<{ success: boolean; message: string; removed: PurgePersonalCalendarResult }>(
    `/api/platform-admin/users/${userId}/personal-calendar`
  );
  return data;
};

// ── Customer Management ──

export interface AdminCustomer {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  gender: string | null;
  birthday: string | null;
  membership_level: string;
  total_spent: number;
  visit_count: number;
  last_visit_date: string | null;
  note: string | null;
  tags: string[] | null;
  is_active: boolean;
  user_id: string;
  tenant_name: string | null;
  tenant_email: string | null;
  created_at: string;
  updated_at: string;
}

const fetchAdminCustomers = async (params?: { search?: string; membership_level?: string; page?: number; limit?: number }) => {
  const { data } = await platformAdminClient.get<{ success: boolean; customers: AdminCustomer[]; total: number }>("/api/platform-admin/customers", { params });
  return data;
};

const fetchAdminCustomer = async (customerId: string) => {
  const { data } = await platformAdminClient.get<{ success: boolean; customer: AdminCustomer }>(`/api/platform-admin/customers/${customerId}`);
  return data.customer;
};

const updateAdminCustomer = async (customerId: string, payload: Partial<Pick<AdminCustomer, "name" | "phone" | "email" | "gender" | "birthday" | "membership_level" | "note" | "is_active">>) => {
  const { data } = await platformAdminClient.put<{ success: boolean; customer: AdminCustomer }>(`/api/platform-admin/customers/${customerId}`, payload);
  return data.customer;
};

const resetAdminCustomerPassword = async (customerId: string, password: string) => {
  const { data } = await platformAdminClient.put<{ success: boolean; message: string }>(`/api/platform-admin/customers/${customerId}/password`, { password });
  return data;
};

const recalcAdminCustomerStats = async (tenantId?: string) => {
  const { data } = await platformAdminClient.post<{
    success: boolean;
    message: string;
    total: number;
    succeeded: number;
    failed: { tenant_id: string; name: string; error: string }[];
  }>("/api/platform-admin/customers/recalculate-stats", tenantId ? { tenant_id: tenantId } : {}, {
    timeout: 600000,
  });
  return data;
};

const resetAdminCustomerLoyaltyPoints = async (tenantId: string) => {
  const { data } = await platformAdminClient.post<{
    success: boolean;
    message: string;
    zeroed_accounts: number;
    total_accounts: number;
  }>("/api/platform-admin/customers/reset-loyalty-points", { tenant_id: tenantId }, {
    timeout: 600000,
  });
  return data;
};

const importAdminCustomers = async (file: File, tenantId: string) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("tenant_id", tenantId);
  const { data } = await platformAdminClient.post<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: string[];
  }>("/api/platform-admin/customers/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return data;
};

const importAdminBookings = async (file: File, tenantId: string, shopId?: string) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("tenant_id", tenantId);
  if (shopId) formData.append("shop_id", shopId);
  const { data } = await platformAdminClient.post<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: string[];
  }>("/api/platform-admin/bookings/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
  });
  return data;
};

export interface TenantShop {
  id: string;
  name: string;
  address: string | null;
  is_active: boolean;
}

const fetchTenantShops = async (tenantId: string): Promise<TenantShop[]> => {
  const { data } = await platformAdminClient.get<{ success: boolean; shops: TenantShop[] }>(
    `/api/platform-admin/tenants/${tenantId}/shops`
  );
  return data.shops;
};

const syncBookingShop = async (tenantId: string, shopId: string, overwrite = false) => {
  const { data } = await platformAdminClient.post<{ success: boolean; updated: number }>(
    "/api/platform-admin/bookings/sync-shop",
    { tenant_id: tenantId, shop_id: shopId, overwrite },
    { timeout: 120000 }
  );
  return data;
};

const migrateBookingLineId = async (tenantId: string, deleteOrphans = false) => {
  const { data } = await platformAdminClient.post<{ success: boolean; updated: number; deleted: number }>(
    "/api/platform-admin/bookings/migrate-line-id",
    { tenant_id: tenantId, delete_orphans: deleteOrphans },
    { timeout: 120000 }
  );
  return data;
};

const importAdminServices = async (file: File, tenantId: string) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("tenant_id", tenantId);
  const { data } = await platformAdminClient.post<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: string[];
  }>("/api/platform-admin/services/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return data;
};

const importAdminProducts = async (file: File, tenantId: string) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("tenant_id", tenantId);
  const { data } = await platformAdminClient.post<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: string[];
  }>("/api/platform-admin/products/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return data;
};

// ─── Tenant System Settings (for tenant portal config) ───

export interface TenantSystemSettings {
  loyaltyPointsEnabled?: boolean;
  loyaltyPointsRedemptionRate?: number;
}

export const fetchTenantSystemSettings = async (tenantId: string) => {
  const { data } = await platformAdminClient.get<{ success: boolean; settings: TenantSystemSettings }>(
    `/api/platform-admin/tenants/${tenantId}/system-settings`
  );
  return data;
};

export const updateTenantSystemSettings = async (tenantId: string, settings: Partial<TenantSystemSettings>) => {
  const { data } = await platformAdminClient.put<{ success: boolean; message: string }>(
    `/api/platform-admin/tenants/${tenantId}/system-settings`,
    settings
  );
  return data;
};

// ─── Tenant Points & Referral ───

export interface TenantPointsInfo {
  balance: number;
  total_earned: number;
  total_used: number;
}

export interface TenantReferralCodeInfo {
  id: string;
  code: string;
  is_active: boolean;
  used_count: number;
  max_uses: number | null;
  created_at: string;
}

const fetchTenantPoints = async (tenantId: string) => {
  const { data } = await platformAdminClient.get<{
    success: boolean;
    points: TenantPointsInfo;
    referralCode: TenantReferralCodeInfo | null;
  }>(`/api/platform-admin/tenants/${tenantId}/points`);
  return data;
};

const resetTenantReferralCode = async (tenantId: string) => {
  const { data } = await platformAdminClient.post<{
    success: boolean;
    referralCode: TenantReferralCodeInfo;
  }>(`/api/platform-admin/tenants/${tenantId}/reset-referral-code`);
  return data;
};

const adjustTenantPoints = async (userId: string, amount: number, description?: string) => {
  const { data } = await platformAdminClient.post<{
    success: boolean;
    points: TenantPointsInfo;
  }>(`/api/platform-admin/points/adjust`, { user_id: userId, amount, description });
  return data;
};

const sendTestPush = async (tenantId: string): Promise<{ success: boolean; message: string; sent: number }> => {
  const { data } = await platformAdminClient.post(
    `/api/platform-admin/tenants/${tenantId}/test-push`
  );
  return data;
};

const sendTenantEmail = async (
  tenantId: string,
  payload: { subject: string; content: string }
): Promise<{ success: boolean; message: string; sent: boolean }> => {
  const { data } = await platformAdminClient.post(
    `/api/platform-admin/tenants/${tenantId}/send-email`,
    payload
  );
  return data;
};

// ─── Notification Triggers (手動觸發通知) ───

export interface NotificationTriggerResult {
  success: boolean;
  message: string;
  sent: number;
  skipped: number;
}

const triggerBookingReminders = async (tenantId: string): Promise<NotificationTriggerResult> => {
  const { data } = await platformAdminClient.post<NotificationTriggerResult>(
    `/api/platform-admin/tenants/${tenantId}/trigger-booking-reminders`
  );
  return data;
};

const triggerDepositReminders = async (tenantId: string): Promise<NotificationTriggerResult> => {
  const { data } = await platformAdminClient.post<NotificationTriggerResult>(
    `/api/platform-admin/tenants/${tenantId}/trigger-deposit-reminders`
  );
  return data;
};

export interface ReferralSetting {
  setting_key: string;
  setting_value: string;
  description: string | null;
  updated_at: string;
}

export const fetchReferralSettings = async () => {
  const { data } = await platformAdminClient.get<{ success: boolean; settings: ReferralSetting[] }>(
    "/api/platform-admin/referral-settings"
  );
  return data;
};

export const updateReferralSettings = async (settings: Record<string, string>) => {
  const { data } = await platformAdminClient.put<{ success: boolean; settings: ReferralSetting[] }>(
    "/api/platform-admin/referral-settings",
    { settings }
  );
  return data;
};

// Scheduler settings
export interface SchedulerSettings {
  birthday_coupon_hour: string;
  subscription_check_hour: string;
  booking_reminder_interval: string;
  booking_reminder_trigger_hour: string;
  grace_period_days: string;
  upcoming_reminder_days: string;
}

export const fetchSchedulerSettings = async () => {
  const { data } = await platformAdminClient.get<{ success: boolean; settings: SchedulerSettings }>(
    "/api/platform-admin/scheduler-settings"
  );
  return data;
};

export const updateSchedulerSettings = async (settings: Partial<SchedulerSettings>) => {
  const { data } = await platformAdminClient.put<{ success: boolean; message: string }>(
    "/api/platform-admin/scheduler-settings",
    { settings }
  );
  return data;
};

export type TenantTag = "tester" | "normal" | "early_bird" | null;

export const updateTenantTag = async (tenantId: string, tag: TenantTag) => {
  const { data } = await platformAdminClient.put<{ success: boolean; message: string }>(
    `/api/platform-admin/tenants/${tenantId}/tag`,
    { tag }
  );
  return data;
};

export const PlatformAdminApi = {
  login: loginPlatformAdmin,
  logout: logoutPlatformAdmin,
  stats: fetchPlatformAdminStats,
  tenants: {
    list: fetchPlatformTenants,
    modules: fetchPlatformTenantModules,
    updateModule: updatePlatformTenantModule,
    updateSubscription: updatePlatformTenantSubscription,
    cancelSubscription: cancelPlatformTenantSubscription,
    systemSettings: fetchTenantSystemSettings,
    updateSystemSettings: updateTenantSystemSettings,
    testPush: sendTestPush,
    sendEmail: sendTenantEmail,
    triggerBookingReminders,
    triggerDepositReminders,
    points: fetchTenantPoints,
    resetReferralCode: resetTenantReferralCode,
    adjustPoints: adjustTenantPoints,
    updateTag: updateTenantTag,
    shops: fetchTenantShops,
  },
  plans: {
    list: fetchPlatformSubscriptionPlans,
    create: createPlatformSubscriptionPlan,
    update: updatePlatformSubscriptionPlan,
    delete: deletePlatformSubscriptionPlan,
    syncTenants: syncPlatformSubscriptionPlanToTenants
  },
  modules: {
    list: fetchPlatformModules
  },
  trialModules: {
    get: fetchTrialModulesConfig,
    update: updateTrialModulesConfig
  },
  trialFeatureToggles: {
    get: fetchTrialFeatureToggles,
    update: updateTrialFeatureToggles
  },
  trialMarketingDisplay: {
    get: fetchTrialMarketingDisplay,
    update: updateTrialMarketingDisplay
  },
  trialLimits: {
    get: fetchTrialLimitsConfig,
    update: updateTrialLimitsConfig
  },
  websiteContent: {
    get: fetchWebsiteContentConfig,
    update: updateWebsiteContentConfig
  },
  wallets: {
    list: fetchWalletAccounts,
    transactions: fetchPlatformWalletTransactions,
    adjust: adjustWalletAccount
  },
  subscriptionRequests: {
    list: fetchSubscriptionRequests,
    approve: approveSubscriptionRequest,
    reject: rejectSubscriptionRequest,
    cancel: cancelSubscriptionRequest
  },
  subscriptionAddonRequests: {
    list: fetchSubscriptionAddonRequests,
    requestPayment: requestSubscriptionAddonPayment,
    approve: approveSubscriptionAddonRequest,
    reject: rejectSubscriptionAddonRequest
  },
  paymentReports: {
    list: fetchPlatformPaymentReports,
    listForTenant: fetchTenantPaymentReports,
    confirm: confirmPaymentReport,
    reject: rejectPaymentReport
  },
  subscriptionPaymentChannels: {
    list: fetchSubscriptionPaymentChannelsAdmin,
    create: createSubscriptionPaymentChannel,
    update: updateSubscriptionPaymentChannel
  },
  paymentMethods: {
    list: fetchPlatformPaymentMethods,
    create: createPlatformPaymentMethod,
    update: updatePlatformPaymentMethod
  },
  linepay: {
    listConfigs: fetchLinepayConfigs,
    getConfig: fetchLinepayConfig,
    saveConfig: saveLinepayConfig,
    verify: verifyLinepayConfig,
    toggle: toggleLinepayConfig
  },
  creditcard: {
    listConfigs: fetchCreditcardConfigs,
    getConfig: fetchCreditcardConfig,
    saveConfig: saveCreditcardConfig,
    verify: verifyCreditcardConfig,
    toggle: toggleCreditcardConfig
  },
  integrationRequests: {
    list: fetchIntegrationRequests,
    detail: fetchIntegrationRequestDetail,
    createForTenant: createIntegrationRequestForTenant,
    configure: configureIntegrationRequest,
    verify: verifyIntegrationRequest,
    complete: completeIntegrationRequest,
    reject: rejectIntegrationRequest,
    reconfigure: reconfigureIntegrationRequest
  },
  menuTree: {
    fetch: fetchGlobalMenuTree,
    save: saveGlobalMenuTree,
    createSection: createMenuSection,
    updateSection: updateMenuSection,
    deleteSection: deleteMenuSection,
    fetchTenantOverrides: fetchTenantMenuOverrides,
    saveTenantOverrides: saveTenantMenuOverrides
  },
  support: {
    list: fetchAdminSupportTickets,
    detail: fetchAdminSupportTicket,
    reply: replyAdminSupportTicket,
    updateStatus: updateAdminTicketStatus,
    executeAccountDeletion: executeAdminAccountDeletion
  },
  users: {
    list: fetchAdminUsers,
    detail: fetchAdminUser,
    update: updateAdminUser,
    resetPassword: resetAdminUserPassword,
    purgePersonalCalendar: purgeAdminUserPersonalCalendar,
  },
  customers: {
    list: fetchAdminCustomers,
    detail: fetchAdminCustomer,
    update: updateAdminCustomer,
    resetPassword: resetAdminCustomerPassword,
    import: importAdminCustomers,
    recalcStats: recalcAdminCustomerStats,
    resetLoyaltyPoints: resetAdminCustomerLoyaltyPoints,
  },
  bookings: {
    import: importAdminBookings,
    syncShop: syncBookingShop,
    migrateLineId: migrateBookingLineId,
  },
  services: {
    import: importAdminServices,
  },
  products: {
    import: importAdminProducts,
  },
  referralSettings: {
    get: fetchReferralSettings,
    update: updateReferralSettings
  },
  schedulerSettings: {
    get: fetchSchedulerSettings,
    update: updateSchedulerSettings
  }
};
